import { Building2, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { ApiError, listPlatformOrgs } from "@/api/client";
import type { PlatformOrg } from "@/api/client";
import { LICENCE, day } from "@/screens/platform/licence";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { EmptyState } from "@/ui/EmptyState";
import { Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { PageHeader } from "@/ui/PageHeader";
import { Skeleton } from "@/ui/primitives";
import { Table, Td, Th } from "@/ui/Table";

function matches(org: PlatformOrg, text: string): boolean {
  const needle = text.trim().toLowerCase();
  if (!needle) return true;
  return [org.name, org.slug, ...org.owners].some((s) => s.toLowerCase().includes(needle));
}

/**
 * Every customer organization, for Darkstar staff (Phase 6 Task 8, ADR-0015): who is in
 * it and what its licence allows. Never its sets or drawings.
 */
export function OrganizationsScreen() {
  const [orgs, setOrgs] = useState<PlatformOrg[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let live = true;
    listPlatformOrgs()
      .then((found) => live && setOrgs(found))
      .catch(
        (err: unknown) =>
          live && setError(err instanceof ApiError ? err.detail : "Could not reach the server."),
      );
    return () => {
      live = false;
    };
  }, []);

  const shown = orgs?.filter((org) => matches(org, query)) ?? [];

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-8 lg:px-8">
      <PageHeader
        title="Organizations"
        description="Every customer organization: who is in it, and what its licence allows."
        actions={
          <Button asChild variant="primary">
            <Link to="/platform/orgs/new">
              <Plus aria-hidden="true" />
              New organization
            </Link>
          </Button>
        }
      />

      {error ? (
        <Notice tone="danger" className="mt-4">
          {error}
        </Notice>
      ) : null}

      <div className="mt-6">
        {orgs === null ? (
          error ? null : (
            <div className="space-y-2" role="status" aria-label="Loading organizations">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )
        ) : orgs.length === 0 ? (
          <EmptyState icon={<Building2 />} title="No organizations yet">
            An organization appears here once it is created.
          </EmptyState>
        ) : (
          <>
            <div className="mb-3 max-w-sm" role="search">
              <Input
                type="search"
                icon={<Search />}
                aria-label="Search organizations"
                placeholder="Search by name, slug or owner"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            <Table aria-label="Organizations">
              <thead>
                <tr>
                  <Th>Organization</Th>
                  <Th>Licence</Th>
                  <Th>Seats</Th>
                  <Th className="hidden md:table-cell">Owners</Th>
                  <Th className="hidden lg:table-cell">Last sign-in</Th>
                  <Th className="hidden lg:table-cell">Created</Th>
                </tr>
              </thead>
              <tbody>
                {shown.map((org) => {
                  const used = org.members + org.pending_invites;
                  return (
                    <tr key={org.id}>
                      <Td>
                        <Link
                          to={`/platform/orgs/${org.id}`}
                          className="block font-medium text-ink hover:text-accent"
                        >
                          {org.name}
                        </Link>
                        <span className="block text-xs text-muted">{org.slug}</span>
                      </Td>
                      <Td>
                        {org.licence ? (
                          <>
                            <Badge tone={LICENCE[org.licence.status].tone}>
                              {LICENCE[org.licence.status].label}
                            </Badge>
                            <span className="mt-1 block text-xs text-muted">
                              ends {day(org.licence.ends_at)}
                            </span>
                          </>
                        ) : (
                          <span className="text-muted">No licence</span>
                        )}
                      </Td>
                      <Td className="whitespace-nowrap text-ink-2">
                        {org.licence ? `${used} of ${org.licence.seats}` : used}
                      </Td>
                      <Td className="hidden text-ink-2 md:table-cell">
                        {org.owners.length > 0 ? (
                          org.owners.map((email) => (
                            <span key={email} className="block">
                              {email}
                            </span>
                          ))
                        ) : (
                          <span className="text-muted">No owner yet</span>
                        )}
                      </Td>
                      <Td className="hidden text-ink-2 lg:table-cell">
                        {day(org.last_sign_in_at)}
                      </Td>
                      <Td className="hidden text-ink-2 lg:table-cell">{day(org.created_at)}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <p className="mt-3 text-xs text-muted">
              {shown.length === orgs.length
                ? `${orgs.length} ${orgs.length === 1 ? "organization" : "organizations"}`
                : `${shown.length} shown of ${orgs.length}`}
            </p>
          </>
        )}
      </div>
    </main>
  );
}
